<template>
  <div class="personal-data__card card">
    <div class="row">
      <div class="col col-xs-12">
        <div class="personal-data__subtitle">
          Links to social networks
        </div>
      </div>

      <div
        class="col col-xs-12"
        v-for="(social, index) in socialArray"
        :key="index"
      >
        <el-form label-position="top">
          <div class="row">
            <div class="col col-xs-11 col-md-4">
              <el-form-item :error="social.networkIdError">
                <el-select
                  class="wide"
                  v-model="social.socialNetworkId"
                  placeholder="Choose a social network"
                  @change="validateSocial(social)"
                  :disabled="Boolean(social.userSocialNetworkId)"
                >
                  <el-option
                    v-for="socialItem in socialDescriptions"
                    :key="socialItem.value"
                    :label="socialItem.description"
                    :value="socialItem.value"
                  >
                  </el-option>
                </el-select>
              </el-form-item>
            </div>

            <div
              class="col col-xs-1 col-md-1 col-md-last col-lg-last col-xl-last col-xxl-last"
            >
              <confirmation-dialog
                content="Are you sure to delete this social network?"
                @on-confirm="removeSocial(social)"
              >
                <el-button
                  class="personal-data__remove-social-button"
                  type="primary"
                >
                <el-icon><Delete/></el-icon>
              </el-button>
              </confirmation-dialog> 
            </div>

            <div class="col col-xs-11 col-md-7">
              <el-form-item :error="social.accountError">
                <el-input
                  v-model="social.accountLink"
                  placeholder="Link to your social network profile"
                  @blur="validateSocial(social)"
                ></el-input>
              </el-form-item>
            </div>
          </div>
        </el-form>
      </div>

      <div class="col col-xs-12">
        <el-button type="primary" @click="addSocial">
          <el-icon><Plus/></el-icon>
          Add social network
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { showMessage } from '@/utils/messages.js';
import {
  getSocialsTypeDescriptions,
  addUserSocial,
  updateUserSocial,
  removeUserSocial,
} from '@/api/profile.js';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import { parseErrorsByFields, removeArrayElementById } from '@/utils/index.js';
import { GET_USER_CONTACT_INFO } from '@/store/mutationTypes.js';
import { Delete as DeleteIcon } from '@element-plus/icons-vue';

export default {
  name: 'EditSocialNetwork',
  props: {
    socialNetworks: {
      type: Array,
      required: false,
    },
  },
  components: {
    ConfirmationDialog,
    DeleteIcon,
  },
  data() {
    return {
      socialArray: [],
      socialDescriptions: null,
    };
  },
  methods: {
    showMessage,
    parseErrorsByFields,
    removeArrayElementById,
    async fetchSocialDescriptions() {
      const { data, success } = await getSocialsTypeDescriptions('User');

      if (success) {
        this.socialDescriptions = data.items;
      }
    },
    async requestAddNewSocial(social) {
      const { accountLink, socialNetworkId } = social;
      const { data, success, errors } = await addUserSocial({
        accountLink: this.getAccountNameByLink(accountLink),
        socialNetworkId,
      });

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'New social network has been added',
        });
        this.$store.dispatch(GET_USER_CONTACT_INFO);
      } else if (errors) {
        this.addSocialErrors(social, errors);
      }
    },
    async requestUpdateSocial(social) {
      const { accountLink, userSocialNetworkId } = social;
      const { data, success, errors } = await updateUserSocial({
        accountLink: this.getAccountNameByLink(accountLink),
        userSocialNetworkId,
      });

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Social network has been updated',
        });
        this.$store.dispatch(GET_USER_CONTACT_INFO);
      } else if (errors) {
        this.addSocialErrors(social, errors);
      }
    },
    async requestRemoveSocial(social) {
      const { userSocialNetworkId } = social;
      const { data, success, errors } = await removeUserSocial({
        userSocialNetworkId,
      });

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Social network has been removed',
        });
        this.$store.dispatch(GET_USER_CONTACT_INFO);
      }
    },
    getAccountNameByLink(link) {
      let accountLink = link;
      if (link.includes('/')) {
        const accounLinkSplited = link.split('/');
        accountLink = accounLinkSplited[accounLinkSplited.length - 1];
      }
      return accountLink;
    },
    setSocials(socials) {
      this.socialArray = socials.map((social) => {
        return {
          accountLink: social.accountLink,
          userSocialNetworkId: social.userSocialNetworkId,
          accountError: null,
          networkIdError: null,
          socialNetworkId: social.socialNetworkId,
        };
      });
    },
    addSocial() {
      this.socialArray.push({
        userSocialNetworkId: null,
        accountLink: null,
        accountError: null,
        networkIdError: null,
        socialNetworkId: null,
      });
    },
    removeSocial(social) {
      const { socialArray, socialNetworks } = this;
      const socialIndex = socialArray.indexOf(social);
      this.socialArray = this.removeArrayElementById(socialArray, socialIndex);

      if (socialNetworks) {
        const isSocialAlreadyAdded = socialNetworks.some(
          (socialNetwork) =>
            socialNetwork.userSocialNetworkId === social.userSocialNetworkId,
        );
        if (isSocialAlreadyAdded) {
          this.requestRemoveSocial(social);
        }
      }
    },
    clearSocialErrors() {
      this.socialArray.forEach((social) => {
        social.accountError = null;
        social.networkIdError = null;
      });
    },
    validateSocial(social) {
      if (!social.accountLink || !social.accountLink.trim()) {
        social.accountError = 'Please enter your profile link';
      } else {
        social.accountError = null;
      }

      if (!social.socialNetworkId) {
        social.networkIdError = 'Please choose social network';
      } else {
        social.networkIdError = null;
      }

      if (
        social.accountLink &&
        social.accountLink.trim() &&
        social.socialNetworkId
      ) {
        social.accountError = null;
        social.networkIdError = null;

        if (social.userSocialNetworkId) {
          const currectSocialNetwork = this.socialNetworks.find(
            (socialNetwork) =>
              socialNetwork.userSocialNetworkId === social.userSocialNetworkId,
          );
          const isSocialDataUpdated =
            currectSocialNetwork.accountLink !== social.accountLink;

          if (isSocialDataUpdated) {
            this.requestUpdateSocial(social);
          }
        } else {
          this.requestAddNewSocial(social);
        }
      }
    },
    addSocialErrors(social, errors) {
      const socialErrors = this.parseErrorsByFields(errors);

      social.accountError = socialErrors.accountLink
        ? socialErrors.accountLink
        : null;
      social.networkIdError = socialErrors.userSocialNetworkId
        ? socialErrors.userSocialNetworkId
        : null;
    },
  },
  created() {
    this.fetchSocialDescriptions();

    const { socialNetworks } = this;
    if (socialNetworks && socialNetworks.length > 0) {
      this.setSocials(socialNetworks);
    }
  },
  watch: {
    socialNetworks(newVal) {
      if (newVal && newVal.length > 0) {
        this.setSocials(newVal);
      }
    },
  },
};
</script>
